import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Copy, Check, Link as LinkIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

const ROLE_MAP = {
  admin:          { label: 'Administrador',  color: 'var(--red)', bg: 'var(--red-dim)' },
  technician:     { label: 'Soporte',        color: 'var(--accent)', bg: '#E6F1FB' },
  change_manager: { label: 'Change Manager', color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  manager:    { label: 'Gerente',          color: 'var(--ink-2)', bg: 'var(--bg-subtle)' },
  viewer:     { label: 'Visor',            color: 'var(--ink-2)', bg: 'var(--bg)' },
  usuario:    { label: 'Usuario',          color: 'var(--green)', bg: 'var(--green-dim)' },
  seguridad:  { label: 'Seguridad',        color: '#B0291D', bg: 'var(--red-dim)' },
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: '', full_name: '', role: 'technician', password: '', phone: '', extra_roles: [] });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetTokens, setResetTokens] = useState([]);
  const [copied, setCopied] = useState('');

  // Sync de integraciones
  const [syncSources, setSyncSources] = useState([]); // [{ key, label, icon }]
  const [syncing, setSyncing] = useState(null); // key de la fuente que está sincronizando
  const [syncMsg, setSyncMsg] = useState(null);
  const [lastSyncMap, setLastSyncMap] = useState({}); // { key: lastSync }

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Cargar fuentes de sync habilitadas al montar
  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/ai-actions/m365-config`, { headers: h }).then(r => r.json()).catch(() => null),
      fetch(`${API_URL}/ai-actions/m365-sync-config`, { headers: h }).then(r => r.json()).catch(() => null),
    ]).then(([m365Cfg, m365Sync]) => {
      const sources = [];
      if (m365Cfg?.configured && m365Sync?.sync_users_enabled) {
        sources.push({ key: 'm365', label: 'Microsoft 365', icon: '☁' });
        if (m365Sync.last_result) {
          setLastSyncMap(prev => ({ ...prev, m365: { ...m365Sync.last_result, synced_at: m365Sync.last_sync } }));
        }
      }
      setSyncSources(sources);
    });
  }, []); // eslint-disable-line

  const runSync = async (key) => {
    setSyncing(key); setSyncMsg(null);
    try {
      const endpointMap = { m365: `${API_URL}/ai-actions/m365-sync-users` };
      const r = await fetch(endpointMap[key], { method: 'POST', headers });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setLastSyncMap(prev => ({ ...prev, [key]: d }));
      setSyncMsg({ ok: true, text: `Sync completado: ${d.created} creados · ${d.updated} actualizados · ${d.deactivated} desactivados${d.errors ? ` · ${d.errors} errores` : ''}` });
      load();
    } catch (e) {
      setSyncMsg({ ok: false, text: e.message });
    } finally {
      setSyncing(null);
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/auth/users`, { headers })
      .then(r => r.json())
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .finally(() => setLoading(false));
    fetch(`${API_URL}/auth/reset-tokens`, { headers })
      .then(r => r.json())
      .then(data => setResetTokens(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const copyLink = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopied(id);
    setTimeout(() => setCopied(''), 2500);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const url = editUser ? `${API_URL}/auth/users/${editUser.id}` : `${API_URL}/auth/users`;
      const method = editUser ? 'PATCH' : 'POST';
      const body = editUser
        ? { full_name: form.full_name, role: form.role, extra_roles: form.extra_roles, phone: form.phone || null, mobile_access: !!form.mobile_access, ...(form.password ? { password: form.password } : {}) }
        : form;
      const r = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      setSuccess(editUser ? 'Usuario actualizado.' : 'Usuario creado.');
      setTimeout(() => setSuccess(''), 3000);
      setShowNew(false);
      setEditUser(null);
      setForm({ email: '', full_name: '', role: 'technician', password: '', phone: '', extra_roles: [], mobile_access: false });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (u) => {
    if (u.id === user.id) return;
    try {
      await fetch(`${API_URL}/auth/users/${u.id}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({ is_active: !u.is_active })
      });
      load();
    } catch (e) { alert(e.message); }
  };

  const handleReset2fa = async (u) => {
    if (!window.confirm(`¿Resetear el 2FA de ${u.full_name}? Deberá configurarlo de nuevo.`)) return;
    try {
      const r = await fetch(`${API_URL}/auth/users/${u.id}/2fa/reset`, { method: 'POST', headers });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      setSuccess(`2FA de ${u.full_name} reseteado.`);
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (e) { alert(e.message); }
  };

  const handleDelete = async (u) => {
    if (u.id === user.id) return alert('No podés eliminar tu propio usuario.');
    if (!window.confirm(`¿Eliminar a ${u.full_name}?`)) return;
    try {
      await fetch(`${API_URL}/auth/users/${u.id}`, { method: 'DELETE', headers });
      load();
    } catch (e) { alert(e.message); }
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email, full_name: u.full_name, role: u.role, password: '', phone: u.phone || '', extra_roles: u.extra_roles || [], mobile_access: !!u.mobile_access });
    setShowNew(true);
    setError('');
  };

  const closeModal = () => {
    setShowNew(false);
    setEditUser(null);
    setForm({ email: '', full_name: '', role: 'technician', password: '', phone: '', extra_roles: [], mobile_access: false });
    setError('');
  };

  const toggleExtraRole = (role) => {
    setForm(f => ({
      ...f,
      extra_roles: f.extra_roles.includes(role)
        ? f.extra_roles.filter(r => r !== role)
        : [...f.extra_roles, role],
    }));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('es-AR') : '—';

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Usuarios</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>Gestion de accesos al sistema</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowNew(true); setEditUser(null); setError(''); }}>
          + Nuevo usuario
        </button>
      </div>

      {/* Panel sincronización de integraciones */}
      {syncSources.length > 0 && (
        <div style={{ border: '1px solid #C7D7F8', borderRadius: 10, padding: '14px 18px', marginBottom: 20, background: '#F0F5FF' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#3b5fc0', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Sincronización de usuarios
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {syncSources.map(src => {
              const last = lastSyncMap[src.key];
              return (
                <div key={src.key} style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 18 }}>{src.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a8a' }}>{src.label}</div>
                    {last && (
                      <div style={{ fontSize: 11, color: '#3b5fc0' }}>
                        Último sync: {last.created} creados · {last.updated} actualizados · {last.deactivated} desactivados
                        {last.synced_at && <span style={{ color: 'var(--ink-2)', marginLeft: 6 }}>({new Date(last.synced_at).toLocaleString('es-AR')})</span>}
                      </div>
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => runSync(src.key)}
                    disabled={syncing === src.key}
                    style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                  >
                    {syncing === src.key ? 'Sincronizando...' : '↻ Sincronizar ahora'}
                  </button>
                </div>
              );
            })}
          </div>
          {syncMsg && (
            <div style={{ marginTop: 10, fontSize: 12, padding: '6px 12px', borderRadius: 6,
              background: syncMsg.ok ? 'var(--green-dim)' : 'var(--red-dim)',
              color: syncMsg.ok ? 'var(--green)' : '#B0291D' }}>
              {syncMsg.ok ? '✓' : '✗'} {syncMsg.text}
            </div>
          )}
        </div>
      )}

      {success && (
        <div style={{ background: 'var(--green-dim)', border: '1px solid #8DD5A0', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: 'var(--green)', marginBottom: 16 }}>
          ✓ {success}
        </div>
      )}

      {/* ── Solicitudes de recuperación de contraseña pendientes ── */}
      {resetTokens.length > 0 && (
        <div style={{ background: 'var(--amber-dim)', border: '1px solid #fde68a', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <LinkIcon size={15} style={{ color: 'var(--amber)' }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--amber)' }}>
              Solicitudes de recuperación de contraseña pendientes ({resetTokens.length})
            </span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--amber)', margin: '0 0 12px', lineHeight: 1.5 }}>
            Estos usuarios solicitaron restablecer su contraseña. Compartiles el link por Teams, Slack o email interno.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {resetTokens.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--surface)', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-2)' }}>{t.full_name} <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>({t.email})</span></div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                    Solicitado: {new Date(t.created_at).toLocaleString('es-AR')} · Expira: {new Date(t.expires_at).toLocaleString('es-AR')}
                  </div>
                </div>
                <button
                  onClick={() => copyLink(t.reset_url, t.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: copied === t.id ? 'var(--green-dim)' : 'var(--bg-subtle)', border: `1px solid ${copied === t.id ? 'var(--green-dim)' : 'var(--border)'}`, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: copied === t.id ? 'var(--green)' : 'var(--ink-2)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {copied === t.id ? <><Check size={13} /> Copiado</> : <><Copy size={13} /> Copiar link</>}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Usuario</th>
                <th style={{ width: 100 }}>Rol</th>
                <th style={{ width: 100 }}>Estado</th>
                <th style={{ width: 70 }}>2FA</th>
                <th style={{ width: 160 }}>Ultimo acceso</th>
                <th style={{ width: 160 }}>Creado</th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-500)' }}>Sin usuarios registrados.</td></tr>
              ) : users.map(u => {
                const role = ROLE_MAP[u.role] || { label: u.role, color: '#888', bg: '#eee' };
                const isCurrentUser = u.id === user.id;
                return (
                  <tr key={u.id} style={{ opacity: u.is_active ? 1 : 0.5 }}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--surface)', fontSize: 13, fontWeight: 600, flexShrink: 0 }}>
                          {u.full_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>{u.full_name} {isCurrentUser && <span style={{ fontSize: 10, color: 'var(--accent)',}}>Tú</span>}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{u.email}</div>
                          {u.phone && <div style={{ fontSize: 11, color: 'var(--green)' }}>📱 {u.phone}</div>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        <span style={{ color: role.color, fontSize: 11, fontWeight: 500 }}>{role.label}</span>
                        {(u.extra_roles || []).map(r => {
                          const er = ROLE_MAP[r] || { label: r, color: 'var(--ink-2)', bg: 'var(--bg)' };
                          return <span key={r} style={{ color: er.color, fontSize: 10, fontWeight: 500, opacity: 0.8 }}>+{er.label}</span>;
                        })}
                      </div>
                    </td>
                    <td>
                      <span style={{ color: u.is_active ? 'var(--green)' : 'var(--ink-2)', fontSize: 11, fontWeight: 500 }}>
                        {u.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      {u.totp_enabled
                        ? <span style={{ color: 'var(--green)', fontSize: 11, fontWeight: 600 }}>✓ Activo</span>
                        : <span style={{ background: 'var(--bg-subtle)', color: 'var(--ink-3)', padding: '3px 8px', borderRadius: 20, fontSize: 11 }}>—</span>
                      }
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{formatDate(u.last_login)}</td>
                    <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{formatDate(u.created_at)}</td>
                    <td>
                      {!isCurrentUser && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>Editar</button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleToggleActive(u)}
                            style={{ color: u.is_active ? '#D45D00' : 'var(--green)' }}
                          >
                            {u.is_active ? 'Desactivar' : 'Activar'}
                          </button>
                          {u.totp_enabled && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleReset2fa(u)}
                              title="El usuario deberá configurar 2FA nuevamente"
                              style={{ color: 'var(--ink-2)' }}
                            >
                              Resetear 2FA
                            </button>
                          )}
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDelete(u)}
                            style={{ color: 'var(--red)' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showNew && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div className="card" style={{ width: '100%', maxWidth: 480, padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 17, fontWeight: 600 }}>{editUser ? 'Editar usuario' : 'Nuevo usuario'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={closeModal}>✕</button>
            </div>

            {error && (
              <div style={{ background: 'var(--red-dim)', border: '1px solid #F5C0BC', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
                {error}
              </div>
            )}

            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Nombre completo *</label>
                <input className="form-input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required disabled={!!editUser} style={editUser ? { background: '#f5f5f5' } : {}} />
              </div>
              <div className="form-group">
                <label className="form-label">Rol *</label>
                <select className="form-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="usuario">Usuario WhatsApp</option>
                  <option value="rrhh">Recursos Humanos</option>
                  <option value="technician">Soporte</option>
                  <option value="seguridad">Seguridad</option>
                  <option value="manager">Gerente</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              {form.role === 'usuario' && (
                <div className="form-group">
                  <label className="form-label">Teléfono WhatsApp *</label>
                  <input
                    className="form-input"
                    type="tel"
                    placeholder="+5491133691710"
                    value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  />
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>
                    Formato internacional con código de país. Ej: +5491133691710
                  </div>
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Accesos adicionales</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border)' }}>
                  {[
                    { role: 'manager',        label: 'Panel Gerencial',  desc: 'Ve costos, incidentes y KPIs ejecutivos' },
                    { role: 'admin',          label: 'Administración',   desc: 'Gestión de usuarios, baseline y agentes' },
                    { role: 'technician',     label: 'Técnico',          desc: 'Inventario, incidentes y seguridad' },
                    { role: 'change_manager', label: 'Change Manager',   desc: 'Puede aprobar y rechazar solicitudes de cambio (RFC)' },
                  ].filter(opt => opt.role !== form.role).map(opt => (
                    <label key={opt.role} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.extra_roles.includes(opt.role)}
                        onChange={() => toggleExtraRole(opt.role)}
                        style={{ marginTop: 3, flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                  {[
                    { role: 'manager', label: 'Panel Gerencial', desc: 'Ve costos, incidentes y KPIs ejecutivos' },
                    { role: 'admin', label: 'Administración', desc: 'Gestión de usuarios, baseline y agentes' },
                    { role: 'technician', label: 'Técnico', desc: 'Inventario, incidentes y seguridad' },
                  ].filter(opt => opt.role !== form.role).length === 0 && (
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>No hay accesos adicionales disponibles para este rol.</div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{editUser ? 'Nueva contraseña (opcional)' : 'Contraseña (opcional)'}</label>
                <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder={editUser ? 'Dejar vacío para no cambiar' : 'Si usa Google, dejar vacío'} />
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 16, background: 'var(--gray-50)', borderRadius: 6, padding: '8px 12px' }}>
                ℹ Si el usuario va a iniciar sesión con Google (@tier5.com.ar), no es necesario asignar contraseña.
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={!!form.mobile_access}
                    onChange={e => setForm(f => ({ ...f, mobile_access: e.target.checked }))}
                  />
                  Acceso a la app móvil
                </label>
                <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
                  Si está desmarcado, este usuario no podrá iniciar sesión desde la app.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Guardando...' : editUser ? 'Guardar cambios' : 'Crear usuario'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
